/**
 * @fileoverview GET handler for the "/engagement/filter_type" endpoint
 */
import * as Sentry from "@sentry/browser";

import { featureOn } from "@lib/featureFlipper";
import fetch from "@lib/fetch";

import { reactQueryFetcherAdapter } from "../reactQueryFetcherAdapters";
import { engagementFilterType } from "./constants";
import type { EngagementFilterTypeResult, EngagementFilterTypePayload } from "./types";

let shadowImportFailureReported = false;

export const getEngagementFilterType = reactQueryFetcherAdapter(
  engagementFilterType,
  async (signal: AbortSignal, payload: EngagementFilterTypePayload): Promise<EngagementFilterTypeResult> => {
    const { filterKey, appGroupId, displayName, onlyGetContainedFilter, lookBackEnabled, filterConfig } = payload;
    const args = {
      filter_key: filterKey,
      app_group_id: appGroupId,
      display_name: displayName,
      only_get_contained_filter: onlyGetContainedFilter,
      look_back_enabled: lookBackEnabled,
      filter_config: {},
    };
    if (filterConfig?.namespace) {
      args.filter_config = {
        eventName: filterConfig.name,
        eventNamespace: filterConfig.namespace,
      };
    }
    const result: EngagementFilterTypeResult = await fetch("/engagement/filter_type", "POST", args, { signal });

    // Shadow comparison of the static filter type registry against this response
    // (observability only — Sentry/Datadog; see filterTypeParityShadow). Gated and
    // lazily imported so the generated registry costs no bundle bytes and never loads
    // while the flipper is off; the shadow can never affect the returned result.
    if (featureOn("static_filter_type_registry_shadow")) {
      void import("@lib/filterTypes/filterTypeParityShadow")
        .then((shadow) => shadow.shadowCompareFilterType(payload, result))
        .catch((error) => {
          // Never let a shadow load/compare failure interfere with the fetch path —
          // but do report it (once per page load): a silently failed chunk load would
          // otherwise leave the shadow dark while its soak metrics read as clean.
          if (!shadowImportFailureReported) {
            shadowImportFailureReported = true;
            Sentry.captureException(error, {
              fingerprint: ["filter-type-registry-shadow", "import-error"],
            });
          }
        });
    }

    return result;
  }
);
